<!-- eslint-disable vuejs-accessibility/interactive-supports-focus -->
<!-- eslint-disable vuejs-accessibility/no-onchange -->
<!-- eslint-disable vue/no-deprecated-dollar-listeners-api -->

<template>
  <!--
  vue-select (and other packages) don't work with keyboard/screenreader on iOS.
  Mainly because iOS dont let javascript track keys like tab or arrows
  Our workaround is to use the native select on iOS
  -->
  <div v-if="isIOS || isAndroid"
       class="s-select s-select--native"
       :class="$attrs.class"
       :style="$attrs.style"
  >
    <span
      :id="`label-${selectId}`"
      class="s-select__label"
      :class="{ 'sr-only': hideLabel, 'text-gray-300': disabled, 'd-none': isScreenReaderActive && hideLabel }"
      :data-test="testId ? `label-${testId}` : 's-select-label'"
    >{{ selectLabel }}</span>

    <div class="position-relative">
      <!-- native select -->
      <!-- @keydown handlers: prevent bubbling to avoid interfering -->
      <component
        v-bind="nativeSelectAttributes"
        :is="isScreenReaderActive ? 'b-button' : 'div'"
        :id="selectId"
        ref="select"
        :data-test="testId ? `select-${testId}` : 's-select-native'"
        class="s-select__select"
        :class="{
          's-select--small': size === 'small',
          'vs--single': !multiple,
          's-select--multiple vs--multiple': multiple,
          's-select--disabled': disabled,
          's-select--clearable': clearable,
          's-select__input--invalid': isInvalid,
          'w-100': isScreenReaderActive,
        }"
        :tabindex="!disabled || isScreenReaderActive ? 0 : undefined"
        @click="toggleDropdown()"
        @keydown.prevent.space="toggleDropdown()"
        @keydown.prevent.enter="toggleDropdown()"
      >
        <!-- multple select -->
        <template v-if="multiple">
          <div v-if="modelValue && modelValue.length" class="s-select__selected-options vs__selected-options">
            <span v-for="(option, i) in modelValue"
                  :key="`${selectId}-selected-${i}`"
                  class="s-select__selected vs__selected"
            >
              <!-- Use #selected-option slot to set custom selected option label -->
              <slot v-bind="option" name="selected-option">
                <!-- hide label form screenreader, because the deselect button also read out the label -->
                <span aria-hidden="true">{{ getSelectedOptionLabel(option) }}</span>
              </slot>

              <!-- deselect button -->
              <b-button type="button"
                        class="s-select__deselect vs__deselect"
                        @click.prevent.stop="removeSelection(option)"
              >
                <span class="sr-only">{{ $t('accessibility.select.deselect', { option: getSelectedOptionLabel(option) }) }}</span>
                <sdui-icon class="icon-close-o-16 gray-300" />
              </b-button>
            </span>
          </div>
          <span v-else-if="placeholder"
                class="s-select__placeholder vs__selected"
                aria-hidden="true"
                data-test="s-select-placeholder"
          >
            {{ placeholder }}
          </span>
        </template>

        <!-- single select -->
        <!-- If placeholder is set, show placeholder when user opens dropdown -->
        <span v-else-if="valueExists(modelValue) && !(dropdownIsOpen && placeholder)" class="s-select__selected vs__selected">
          <!-- Use #selected-option slot to set custom selected option label -->
          <slot v-bind="selectedOption" name="selected-option">
            {{ getSelectedOptionLabel(modelValue) }}
          </slot>
        </span>
        <span v-else
              class="s-select__placeholder vs__selected"
              aria-hidden="true"
              data-test="s-select-placeholder"
        >
          {{ placeholder }}
        </span>
      </component>

      <!-- native options menu -->
      <div
        v-if="!isScreenReaderActive"
        v-show="dropdownIsOpen"
        :id="`listbox-${selectId}`"
        ref="dropdown"
        class="s-select__dropdown-menu vs__dropdown-menu"
        :class="{ 'vs__dropdown-menu--empty': !options.length }"
        role="listbox"
        :aria-labelledby="`label-${selectId}`"
        tabindex="-1"
      >
        <!-- options -->
        <!-- @keydown handlers: prevent bubbling to avoid interfering -->
        <template v-if="options.length">
          <div
            v-for="(option, i) in options"
            :id="`${selectId}-option-${i}`"
            :key="`${selectId}-option-${i}`"
            :ref="setRefForOption(option)"
            role="option"
            class="s-select__option vs__dropdown-option"
            :class="{ 'vs__dropdown-option--selected': isSelected(option), 'vs__dropdown-option--disabled': isOptionDisabled(option) }"
            :aria-selected="`${isSelected(option)}`"
            :aria-disabled="isOptionDisabled(option) ? 'true' : undefined"
            tabindex="0"
            @click="selectOption(option)"
            @keydown.prevent.space="selectOption(option)"
            @keydown.prevent.enter="selectOption(option)"
            @keydown.prevent.esc="closeDropdownOnEsc"
          >
            <!-- Use #option slot to set custom option label -->
            <slot v-bind="option" name="option">
              <!-- Fallback if #option slot is not defined -->
              {{ typeof option === 'object' ? option[displayLabel] : option }}
            </slot>
          </div>
        </template>

        <!-- no options avaiable -->
        <span v-else class="s-select__option s-select__no-option vs__no-options">
          <slot name="no-options">
            {{ $t('ui.sdui_select.no_data') }}
          </slot>
        </span>
      </div>

      <div class="s-select__actions">
        <!-- clear select button, only show when prop and value are set -->
        <b-button
          v-if="valueExists(modelValue) && clearable && !disabled"
          type="button"
          class="s-select__clear reset-button d-inline-flex align-items-center justify-content-center"
          data-test="s-select-clear"
          @click="clearSelection"
        >
          <sdui-icon class="icon-close-o gray-300" />
          <span class="sr-only">{{ $t('accessibility.select.clear_selection') }}</span>
        </b-button>

        <!-- caret -->
        <sdui-icon v-if="!loading"
                   class="s-select__caret icon-chevron-down gray-300"
                   data-test="s-select-caret"
        />

        <!-- loading indicator -->
        <sdui-icon v-if="loading"
                   class="s-select__loading icon-refresh-o gray-300"
                   data-test="s-select-loading"
        />
      </div>

      <SModal v-model="isScreenReaderSelectModalOpen"
              :header-title="selectLabel"
              :show-footer-gradient="false"
      >
        <template v-if="isScreenReaderSelectModalOpen">
          <fieldset>
            <!-- legend is only here for valid HTML -->
            <legend class="d-none">{{ selectLabel }}</legend>
            <template v-if="multiple">
              <SCheckbox
                v-for="(option, i) in options"
                :key="`${selectId}-option-${i}`"
                :model-value="!!isSelected(option)"
                class="s-select--native-sr"
                data-test="s-select--native-checkbox"
                :label-text="typeof option === 'object' ? String(option[displayLabel]) : String(option)"
                :name="`${selectId}-check-options`"
                :disabled="disabled || isOptionDisabled(option)"
                checkbox-type="square"
                @update:model-value="(selected) => selectCheckboxOption(option, selected)"
              >
                <template #additional-content>
                  <!-- Use #option slot to set custom option label -->
                  <slot v-bind="option" name="option">
                    <!-- Fallback if #option slot is not defined -->
                    <p class="mb-0 ml-3" data-test="additional-content-slot">
                      {{ typeof option === 'object' ? option[displayLabel] : option }}
                    </p>
                  </slot>
                </template>
              </SCheckbox>
            </template>
            <template v-else>
              <SRadioButton
                v-for="(option, i) in options"
                :key="`${selectId}-option-${i}`"
                :ref="setRefForOption(option)"
                :currently-selected-value="selectedRadioButtonValue"
                :value-on-selection="resultKey ? option[resultKey] : option"
                class="s-select--native-sr"
                data-test="s-select--native-radiobtn"
                :label-text="typeof option === 'object' ? String(option[displayLabel]) : String(option)"
                :disabled="disabled || isOptionDisabled(option)"
                :name="`${selectId}-radio-options`"
                radio-button-type="round"
                @change="(selected) => selectRadioBtnOption(selected)"
              >
                <template #additional-content>
                  <!-- Use #option slot to set custom option label -->
                  <slot v-bind="option" name="option">
                    <!-- Fallback if #option slot is not defined -->
                    <p class="mb-0 ml-3" data-test="additional-content-slot">
                      {{ typeof option === 'object' ? option[displayLabel] : option }}
                    </p>
                  </slot>
                </template>
              </SRadioButton>
            </template>
          </fieldset>
        </template>
        <span v-else class="s-select__option s-select__no-option vs__no-options">
          <slot name="no-options">
            {{ $t('ui.sdui_select.no_data') }}
          </slot>
        </span>

        <template #modal-footer>
          <b-button block
                    size="lg"
                    variant="primary"
                    type="submit"
                    @click="submitModalSelection"
          >
            {{ $t('ui.submit') }}
          </b-button>
        </template>
      </SModal>
    </div>

    <!-- invalid text -->
    <p
      v-if="isInvalid"
      :id="`invalid-${selectId}`"
      class="s-select__invalid text-danger-dark nano mt-1 mb-0"
      :data-test="testId ? `invalid-${testId}` : 's-select-invalid-text'"
    >
      {{ invalidFieldMessage }}
    </p>

    <!-- help text -->
    <p
      v-if="helpText"
      :id="`help-${selectId}`"
      class="s-select__help text-gray-300 nano mt-1 mb-0"
      :data-test="testId ? `help-${testId}` : 's-select-help-text'"
    >
      {{ helpText }}
    </p>
  </div>

  <div v-else
       class="s-select__v-select"
       :class="$attrs.class"
       :style="$attrs.style"
  >
    <span
      v-if="!hideLabel"
      class="s-select__label"
      :class="{ 'text-gray-300': disabled }"
      :data-test="testId ? `label-${testId}` : 's-select-label'"
    >
      {{ selectLabel }}
    </span>

    <v-select
      v-bind="filteredAttrs"
      ref="select"
      :model-value="modelValue"
      class="s-select"
      :data-test="testId ? `select-${testId}` : 's-select'"
      :class="{ 's-select--small': size === 'small', 's-select__input--invalid': isInvalid }"
      :reduce="(chosenOption) => (resultKey ? chosenOption[resultKey] : chosenOption)"
      :options="options"
      :placeholder="placeholder"
      :label="displayLabel"
      :multiple="multiple"
      :disabled="disabled"
      :loading="loading"
      :searchable="searchable"
      :clearable="clearable"
      :aria-invalid="isInvalid ? 'true' : undefined"
      :aria-describedby="descriptionId"
      :calculate-position="withPopper"
      :selectable="(option) => !isOptionDisabled(option)"
      append-to-body
      @update:model-value="validateRequired()"
      v-on="$listeners"
    >
      <!-- slot for setting custom selected label -->
      <template #selected-option="slotProps">
        <slot name="selected-option" v-bind="slotProps" />
      </template>

      <!-- slot for setting custom option label -->
      <template #option="slotProps">
        <slot name="option" v-bind="slotProps" />
      </template>

      <!-- caret icon -->
      <template #open-indicator="{ attributes }">
        <sdui-icon v-bind="attributes" class="icon-chevron-down gray-300" />
      </template>

      <!-- no options -->
      <template #no-options>
        {{ $t('ui.sdui_select.no_data') }}
      </template>
    </v-select>

    <!-- invalid text -->
    <p
      v-if="isInvalid"
      :id="`invalid-${selectId}`"
      class="s-select__invalid text-danger-dark nano mt-1 mb-0"
      :data-test="testId ? `invalid-${testId}` : 's-select-invalid-text'"
    >
      {{ invalidFieldMessage }}
    </p>

    <!-- help text -->
    <p
      v-if="helpText"
      :id="`help-${selectId}`"
      class="s-select__help text-gray-300 nano mt-1 mb-0"
      :data-test="testId ? `help-${testId}` : 's-select-help-text'"
    >
      {{ helpText }}
    </p>
  </div>
</template>

<script>
import { createPopper } from '@popperjs/core'
import SRadioButton from 'src/components/base/SRadioButton.vue'
import SCheckbox from 'src/components/base/SCheckbox.vue'
import { Field } from 'vee-validate'

export default {
  // TODO VUE3: Remove compatConfig when @vue/compat is removed
  compatConfig: {
    COMPONENT_V_MODEL: false,
  },

  // Prevent inheriting attributes from parent component to root element of this component.
  // This is to prevent having duplicate data-cy/test or aria attributes on the root element.
  inheritAttrs: false,

  name: 'SSelect',

  components: { SCheckbox, SRadioButton, Field },

  props: {
    // our own props ------------------------------------------------
    modelValue: {
      type: [String, Number, Array, Object],
      default: null,
    },

    // provide accessible label for the select input (for screenreaders)
    selectLabel: {
      type: String,
      required: true,
    },

    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium'].includes(value),
    },

    resultKey: {
      type: String,
      default: undefined,
    },

    placement: {
      type: String,
      default: 'bottom',
      validator: (value) => ['top', 'bottom'].includes(value),
    },

    hideLabel: {
      type: Boolean,
      default: false,
    },

    helpText: {
      type: String,
      default: '',
    },

    testId: {
      type: String,
      default: '',
    },

    compare: {
      type: Function,
      default: null,
    },

    /*
    Value should match a string in "validations.attributes" (see [LANG].json)
     */
    validationFieldName: {
      type: String,
      default: '',
    },

    isRequired: {
      type: Boolean,
      default: false,
    },

    // vue-select props that are currently in use -------------------
    options: {
      type: Array,
      default: () => [],
    },

    placeholder: {
      type: String,
      default: '',
    },

    displayLabel: {
      type: String,
      default: 'label',
    },

    multiple: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    searchable: {
      type: Boolean,
      default: true,
    },

    clearable: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    selectId: null,
    dropdownIsOpen: false,
    popper: null,
    isIOS: false,
    isAndroid: false,
    isScreenReaderActive: false,
    isScreenReaderSelectModalOpen: false,
    selectedRadioButtonValue: null,
    selectedCheckBoxValues: null,
    isFieldDirty: false
  }),

  computed: {
    filteredAttrs() {
      const { class: _cls, style: _style, ...otherAttrs } = this.$attrs
      return otherAttrs
    },

    invalidFieldMessage() {
      if (!this.isInvalid || !this.validationFieldName) return
      const fieldName = this.$t(`validations.attributes.${this.validationFieldName}`)
      return this.$t('validations.inputSelect.required', { fieldName })
    },

    isInvalid() {
      return this.isRequired && this.isFieldDirty && !this.modelValue
    },

    descriptionId() {
      // Always ensure that Screenreader users gets to hear the label text
      if (this.isScreenReaderActive) {
        // is invalid and has help text
        if (this.isInvalid && this.helpText) return `label-${this.selectId} invalid-${this.selectId} help-${this.selectId}`

        // is invalid, but no help text
        if (this.isInvalid) return `label-${this.selectId} invalid-${this.selectId}`

        // is valid and has help text
        if (this.helpText) return `label-${this.selectId} help-${this.selectId}`

        // is valid and no help text
        return `label-${this.selectId} `
      }

      // is invalid and has help text
      if (this.isInvalid && this.helpText) return `invalid-${this.selectId} help-${this.selectId}`

      // is invalid, but no help text
      if (this.isInvalid) return `invalid-${this.selectId}`

      // is valid and has help text
      if (this.helpText) return `help-${this.selectId}`

      // is valid and no help text
      return undefined
    },

    selectedOption() {
      return this.options.find((option) => this.isSelected(option)) || []
    },

    // this is only for aria-activedescendant
    activeDescendant() {
      // there can only be an active descendant if there is a selected option and the value exists
      // check "this.selectedOption === undefined" in case "this.selectedOption === 0"
      if (this.selectedOption === undefined || !this.valueExists(this.modelValue)) return undefined

      const selectedIndex = this.options.findIndex((option) => this.isSelected(option))

      if (selectedIndex < 0) return undefined

      return `${this.selectId}-option-${selectedIndex}`
    },

    nativeSelectAttributes() {
      if (this.isScreenReaderActive) {
        return {
          variant: 'link',
          disabled: this.disabled,
          'aria-disabled': this.disabled,
          'aria-invalid': this.isInvalid ? 'true' : undefined,
          'aria-describedby': this.descriptionId,
          ...this.$attrs,
        }
      }

      return {
        role: 'combobox',
        'aria-controls': `listbox-${this.selectId}`,
        'aria-expanded': `${this.dropdownIsOpen}`,
        'aria-haspopup': 'listbox',
        'aria-labelledby': `label-${this.selectId}`,
        'aria-invalid': this.isInvalid ? 'true' : undefined,
        'aria-describedby': this.descriptionId,
        'aria-disabled': this.disabled,
        'aria-multiselectable': this.multiple,
        'aria-activedescendant': this.activeDescendant,
      }
    },
  },

  emits: ['update:modelValue', 'isValid'],

  methods: {
    transformSelection(value) {
      // Multiple selection case
      if (this.multiple) {
        // vue-select already returns an array with all selected options
        if (Array.isArray(value)) {
          if (this.resultKey) return value.map((item) => item[this.resultKey])
          return value
        }

        const selectedValue = this.resultKey ? value[this.resultKey] : value
        const selectedValueArray = Array.isArray(this.modelValue) ? [...this.modelValue] : []

        // Prevent duplicates
        if (!selectedValueArray.some((item) => this.isSame(item, selectedValue))) {
          selectedValueArray.push(selectedValue)
        }

        return selectedValueArray
      }

      // single selection case
      return this.resultKey ? value[this.resultKey] : value
    },

    setSelected(option) {
      this.emitSelected(this.transformSelection(option))
    },

    emitSelected(value) {
      this.$emit('update:modelValue', value)
    },

    validateRequired () {
      this.isFieldDirty = true
      this.$emit('isValid', !this.isInvalid)
    },

    resetValidationState () {
      this.isFieldDirty = false
    },

    /*
     * Calculate the position of the dropdown.
     * This will try to position the dropdown top/bottom of the toggle, depending on the given space.
     * Copied from the docs: https://vue-select.org/guide/positioning.html#popper-js-integration
     */
    withPopper(dropdownList, component, { width }) {
      dropdownList.setAttribute('data-test', this.testId ? `dropdown-${this.testId}` : 'dropdown-s-select')

      /**
       * Make it easier to overwrite default scss
       */
      dropdownList.classList.add('s-select__dropdown-menu')

      /**
       * We need to explicitly define the dropdown width since
       * it is usually inherited from the parent with CSS.
       */
      dropdownList.style.width = width

      /**
       * Here we position the dropdownList relative to the $refs.toggle Element.
       *
       * The 'offset' modifier aligns the dropdown so that the $refs.toggle and
       * the dropdownList has a gap of 10 pixel.
       *
       * The 'toggleClass' modifier adds a 'drop-up' class to the Vue Select
       * wrapper so that we can set some styles for when the dropdown is placed
       * above.
       */
      const popper = createPopper(component.$refs.toggle, dropdownList, {
        placement: this.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 10],
            },
          },
        ],
      })

      // To prevent memory leaks Popper needs to be destroyed.
      return () => popper && popper.destroy()
    },

    // native select events ----------------------------------------
    valueExists(value) {
      // need to check this way, because the number 0, was also returning false (which can be a valid value for time)
      return !(value === undefined || value === null || value === '')
    },

    isSame(option, val) {
      // Use custom compare function if provided
      if (this.compare) return this.compare(option, val)

      // Check if both are objects
      const bothObjects = typeof option === 'object' && typeof val === 'object'

      // Compare by resultKey if set, otherwise compare the whole object
      if (bothObjects) {
        return this.resultKey ? option[this.resultKey] === val[this.resultKey] : this.$compare(option, val)
      }

      // This case only happens when this.resultKey is set.
      // In this case: The Parent passes down this.options as array of objects and use resultKey to emit only a specific value from an object.
      // Which means that the value could be a string or number (of that returned resultKey, that we emitted).
      if (typeof option === 'object' && this.resultKey) return option[this.resultKey] === val

      // Direct comparison for primitive values
      return option === val
    },

    isSelected(option) {
      // dont bother checking if there is no value at all
      if (!this.valueExists(this.modelValue)) return false

      if (this.multiple) return this.modelValue.find((value) => this.isSame(option, value))

      return this.isSame(option, this.modelValue)
    },

    /**
     * Options given as objects can be marked as not selectable with `disabled: true`.
     */
    isOptionDisabled(option) {
      return typeof option === 'object' && option !== null && !!option.disabled
    },

    selectOption(option) {
      if (this.isOptionDisabled(option)) return

      const selectedValue = this.transformSelection(option)

      this.emitSelected(selectedValue)
      this.closeDropdown()
    },

    selectCheckboxOption(option, isSelected) {
      if (isSelected) return this.selectedCheckBoxValues.push(option)
      this.selectedCheckBoxValues = this.selectedCheckBoxValues.filter((value) => !this.$compare(option, value))
    },

    selectRadioBtnOption(option) {
      this.selectedRadioButtonValue = option
    },

    submitModalSelection() {
      if (!this.selectedCheckBoxValues && !this.selectedRadioButtonValue) {
        this.closeDropdown()
        return
      }
      if (this.multiple) return this.selectOption(this.selectedCheckBoxValues)
      const radioToOptions = this.resultKey
        ? this.options.find((option) => option[this.resultKey] === this.selectedRadioButtonValue)
        : this.selectedRadioButtonValue
      this.selectOption(radioToOptions)
    },

    setRefForOption(option) {
      return this.resultKey ? String(option[this.resultKey]) : String(option)
    },

    getSelectedOptionLabel(value) {
      const valueIsObject = typeof value === 'object'

      // if the value if an object, return the displayLabel of the label
      if (valueIsObject) return value[this.displayLabel]

      if (!this.options.length) return this.$t('ui.sdui_select.no_data')

      // check if all this.options are an array of objects
      const optionsAreObjects = this.options.every((option) => typeof option === 'object')

      // Can happen if parent passes down this.options as array of objects, but passes a primitive value as this.value
      if (optionsAreObjects) {
        // if the value is a primitive value, find the object in this.options and return the displayLabel
        const option = this.options.find((option) => this.isSame(option, value))
        return option[this.displayLabel]
      }

      // if its an primitive value, return the value itself
      return value
    },

    removeSelection(option) {
      const selectedValueArray = this.modelValue.filter((value) => !this.isSame(option, value))
      this.emitSelected(selectedValueArray)
    },

    clearSelection() {
      const value = this.multiple ? [] : null

      this.emitSelected(value)
      this.closeDropdown()
    },

    toggleDropdown() {
      if (this.disabled) return

      if (this.dropdownIsOpen) return this.closeDropdown(false)

      return this.openDropdown()
    },

    async openDropdown() {
      // For screenreaders, we fall back to a modal instead of listbox. Listbox has all kinds of issues
      // on iOS and Android when used with screenreader.
      if (this.isScreenReaderActive) {
        if (this.multiple) {
          // set value for checkbox
          this.selectedCheckBoxValues = this.options.filter((option) => this.isSelected(option))
        } else {
          // set value for radio button
          this.selectedRadioButtonValue = this.modelValue
        }
        // open the modal
        this.isScreenReaderSelectModalOpen = true
      } else {
        // open + position dropdown listbox
        this.dropdownIsOpen = true
        this.positionDropdown()

        // focus first or already selected option
        this.$nextTick(() => {
          // if a value is already selected, focus that instead of the first one
          if (this.selectedOption) {
            this.$refs?.[this.selectedOption]?.[0]?.focus()
            return this.$refs?.[this.selectedOption]?.[0]?.scrollIntoView()
          }

          // focus first option
          const firstOption = this.$refs?.dropdown?.querySelector('.s-select__option')
          if (firstOption) firstOption.focus()
        })
      }
    },

    closeDropdown(reFocusToggle = true) {
      if (this.isScreenReaderActive) {
        // close modal
        this.isScreenReaderSelectModalOpen = false
        this.selectedRadioButtonValue = null
        this.selectedCheckBoxValues = null
      } else {
        // close dropdown
        this.dropdownIsOpen = false

        this.popper && this.popper.destroy()
        this.popper = null

        // prevent loosing focus after closing dropdown
        if (reFocusToggle) this.$refs?.select?.focus()
      }
    },

    closeDropdownOnEsc() {
      if (!this.dropdownIsOpen) return
      this.closeDropdown()
    },

    closeDropdownOnClickOutside(event) {
      if (!this.dropdownIsOpen) return

      const dropdown = this.$refs?.dropdown
      const toggle = this.$refs?.select

      if (dropdown && !dropdown.contains(event.target) && toggle && !toggle.contains(event.target)) {
        this.closeDropdown(false)
      }
    },

    // if keyboard-user leaves dropdown, close it
    handleBlur() {
      // Use setTimeout to wait for the next focused element
      setTimeout(() => {
        const activeElement = document.activeElement
        if (!this.$el.contains(activeElement)) this.closeDropdown(false)
      }, 10)
    },

    positionDropdown() {
      const select = this.$refs?.select
      const dropdown = this.$refs?.dropdown

      if (!select || !dropdown) return

      // position dropdown with popper.js
      this.popper = createPopper(select, dropdown, {
        placement: this.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 10],
            },
          },
        ],
      })
    },
  },

  watch: {
    '$store.state.isScreenReaderActive'(newVal) {
      this.isScreenReaderActive = newVal
    },
  },

  created() {
    this.selectId = this.$uuid()
  },

  /*
  vue-select uses aria-label="Search for option" to label the select input (coming directly from vue-select).
  Sadly vue-select does not provide a way to change this label.
  Which is important for us, because we want to be able to localize this label.
  So until vue-select provides a way to replace this label, we have to do it via "mounted".
  https://github.com/sagalbot/vue-select/blob/master/src/components/Select.vue
  */
  async mounted() {
    if (!import.meta.env.IS_STORYBOOK) {
      this.isIOS = this.$store.state.isIOS
      this.isAndroid = this.$store.state.isAndroid

      // a11y keyboard handling
      if (this.isIOS || this.isAndroid) {
        document.addEventListener('click', this.closeDropdownOnClickOutside)

        await this.$nextTick()
        this.$refs?.dropdown?.addEventListener('blur', this.handleBlur, true)

        return
      }
    }

    await this.$nextTick()
    const dropdownToggle = this.$refs?.select?.$el?.querySelector('.vs__dropdown-toggle')
    if (dropdownToggle) dropdownToggle.setAttribute('aria-label', this.selectLabel)
  },

  beforeMount() {
    this.isScreenReaderActive = import.meta.env.IS_STORYBOOK ? false : this.$store.state.isScreenReaderActive
  },

  beforeUnmount() {
    this.popper && this.popper.destroy()

    if (!this.isIOS || this.isAndroid) return

    document.removeEventListener('click', this.closeDropdownOnClickOutside)
    this.$refs?.dropdown?.removeEventListener('blur', this.handleBlur, true)
  },
}
</script>

<style scoped lang="scss">
.s-select {
  // our own scss variables ----------------------------------------------------------
  --s-height-default: 48px;
  --s-height-small: 40px;

  // overwrite the default scss variables of vue-select ------------------------------
  /* Search Input */
  --vs-search-input-bg: #{$color-secondary};
  --vs-search-input-placeholder-color: #{$color-gray-300};

  /* Disabled State */
  --vs-disabled-bg: #{$color-white};

  /* Borders */
  --vs-border-color: #{$color-gray-100};
  --vs-border-radius: 0.5rem;

  min-width: 90px;
}

.s-select__label {
  display: inline-block;
  font-size: pxtorem(15);
  margin-bottom: 0.5rem;
}

// adding our own custom styles to vue-select ------------------------------------

// adding our own box-shadow
.s-select :deep(.vs__dropdown-toggle) {
  background: var(--vs-search-input-bg);
  min-height: var(--s-height-default); // set our sdui default custom height
  box-shadow: $sdui-shadow;

  &:focus-within {
    border: 1px solid $color-primary;
    box-shadow: 0 0 0 3px $color-primary-light;
  }
}

// invalid state
.s-select__input--invalid,
.s-select__input--invalid :deep(.vs__dropdown-toggle) {
  border-color: $color-danger !important;
  background-color: $color-danger-light !important;
  border-radius: var(--vs-border-radius);

  &:hover {
    border-color: $color-danger !important;
  }
}

// overwrite default vue-select: We always want rounded borders on the toggle
.s-select.vs--open :deep(.vs__dropdown-toggle) {
  border-bottom-color: var(--vs-border-color);
  border-bottom-left-radius: var(--vs-border-radius);
  border-bottom-right-radius: var(--vs-border-radius);

  &:focus-within {
    border-bottom-color: $color-primary;
  }
}

// disabled styling
.s-select.vs--disabled :deep(.vs__dropdown-toggle) {
  background-color: $color-white;
  box-shadow: none;
}

.s-select.vs--disabled :deep(.vs__selected) {
  color: $color-gray-300;
}

// custom heights
.s-select--small :deep(.vs__dropdown-toggle) {
  min-height: var(--s-height-small); // small custom height
}

// add more padding-left
.s-select :deep(.vs__selected-options) {
  padding-left: 13px;
}

// prevent height flicker on select, because of our own default custom height
.s-select :deep(.vs__selected),
.s-select :deep(.vs__search) {
  min-height: calc(var(--s-height-default) - 10px);
  padding-left: 0;
}

// prevent height flicker on select, because of our own small custom height
.s-select--small :deep(.vs__selected),
.s-select--small :deep(.vs__search) {
  min-height: calc(var(--s-height-small) - 10px);
}

// overwrite default vue-select: tags in multiple select
.s-select.vs--multiple :deep(.vs__selected-options) {
  align-items: center;
}

.s-select.vs--multiple :deep(.vs__selected) {
  border-radius: 14px;
  background-color: $color-gray-300;
  color: $color-white;
  padding: 2px 3px 2px 12px;
  min-height: inherit;
}

.s-select__deselect,
.s-select :deep(.vs__deselect) {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $color-white !important;
  border: none !important;
  border-radius: 50%;
  color: $color-gray-300;
  height: 20px;
  margin-left: 8px;
  width: 20px;
  padding: 0;
  min-height: inherit !important;
}

// ensure caret icon has cursor pointer
.s-select :deep(.vs__actions) {
  cursor: pointer;
}

// hide input caret when not open
.s-select.v-select:not(.vs--open) :deep(.vs__search) {
  caret-color: transparent;
}

// Ensure native HTML select looks the same as vue-select --------------------
.s-select__select {
  user-select: none;
  box-shadow: $sdui-shadow;
  min-height: var(--s-height-default);
  text-align: left;
  -webkit-appearance: none;
  padding: 4px 30px 4px 13px;
  display: flex;
  border: 1px solid var(--vs-border-color);
  color: var(--vs-search-input-placeholder-color);
  background: var(--vs-search-input-bg);
  border-radius: var(--vs-border-radius);
  cursor: pointer;
  font-weight: $font-weight-normal;

  &:focus {
    border: 1px solid $color-primary;
    box-shadow: 0 0 0 3px $color-primary-light;
    outline: none !important;
  }
}

.s-select--disabled {
  background-color: $color-white;
  box-shadow: none;
}

.s-select--disabled .s-select__selected {
  color: $color-gray-300;
}

.s-select--clearable {
  padding-right: 60px;
}

.s-select--small {
  min-height: var(--s-height-small);
}

.s-select--multiple {
  display: flex;
}

.s-select--multiple .s-select__selected {
  background-color: var(--s-gray-300);
  border-radius: 14px;
  color: var(--s-white);
  min-height: inherit;
  padding: 2px 3px 2px 12px;
}

.s-select__selected {
  margin: 0;
  padding: 0;
}

.s-select__select .s-select__selected-options {
  align-items: center;
  gap: 4px;
  padding-left: 0;
}

.s-select__placeholder {
  color: var(--vs-search-input-placeholder-color);
  border: none;
  background-color: transparent;
  margin: 0;
  padding: 0;
}

.s-select__actions {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 2px;
  pointer-events: none;
}

.s-select__clear {
  pointer-events: all;
}

.s-select--native-sr {
  :deep(.additional-content) {
    flex-flow: row-reverse;
    margin-bottom: 12px !important;
    width: 100%;
  }

  :deep(.additional-content-wrapper) {
    flex-direction: row !important;
  }
}

// let loading icon rotate
@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.s-select__loading {
  animation: rotate 2s linear infinite;
}

.s-select__option:not(.s-select__no-option):focus-visible {
  background-color: var(--vs-dropdown-option--active-bg);
  color: var(--vs-dropdown-option--active-color);
  text-decoration: underline;
}

.s-select .reset-button,
.s-select .reset-button:hover {
  padding: 0;
  background-color: transparent !important;
  min-height: inherit !important;
  border: none !important;
}
</style>

<!-- vue-select dropdowns styles. Needs to be global because we append the dropdown to the body, not the component itself -->
<style lang="scss">
// overwrite default vue-select: we want the dropdown to be "detached" from the toggle
.s-select__dropdown-menu.vs__dropdown-menu {
  /* Borders */
  --vs-border-color: #{$color-gray-100};
  --vs-border-radius: 0.5rem;

  /* Dropdown */
  --vs-dropdown-color: #{$color-gray-300};
  --vs-dropdown-z-index: 1080;
  --vs-dropdown-box-shadow: 0 5px 20px 0 rgba(0, 0, 0, 0.12) !important;

  /* Options */
  --vs-dropdown-option-padding: 12px 13px;

  /* Active State */
  --vs-dropdown-option--active-bg: #{$color-secondary};
  --vs-dropdown-option--active-color: #{$color-primary};

  border: none;
  border-radius: var(--vs-border-radius);
  padding-top: 0;
  padding-bottom: 0;
  user-select: none;
}

// how the selected option should look like
.s-select__dropdown-menu .vs__dropdown-option--selected {
  background-color: var(--vs-dropdown-option--active-bg);
  color: var(--vs-dropdown-option--active-color);
  font-weight: $font-weight-bold;
}

// options that cannot be selected
.s-select__dropdown-menu .vs__dropdown-option--disabled {
  color: $color-gray-300;
  cursor: not-allowed;
}

// if there are no options
.s-select__dropdown-menu .vs__no-options {
  text-align: left;
  padding: 12px;
  display: block;
}

// ensure the highlighted option (item that has focus) disguishes itself from the selected options
.s-select__dropdown-menu .vs__dropdown-option--highlight {
  text-decoration: underline;
}

// add border between options
.s-select__dropdown-menu .vs__dropdown-option:not(:last-child) {
  border-bottom: 1px solid $color-gray-100;
}
</style>
